import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { LightingFixture } from '@/types/floorPlanViewer';
import { deleteEmptyCircuit } from './floorPlanService';
import { logger } from '@/lib/logger';

/**
 * Fetch lighting fixtures for a floor plan
 */
export async function getFixtures(floorPlanId: string): Promise<LightingFixture[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_fixtures')
    .select('*')
    .eq('floor_plan_id', floorPlanId);

  if (error) throw error;
  return data as unknown as LightingFixture[];
}

/**
 * Create a new lighting fixture
 *
 * Automatically applies saved default scales if:
 * 1. No scale is explicitly provided
 * 2. Scale is 1.0 (the default value)
 * 3. A default exists for this category/subcategory
 */
export async function createFixture(
  fixture: Omit<LightingFixture, 'id' | 'created_at'>,
): Promise<LightingFixture> {
  // Check for default scale if not provided or if using default 1.0
  let finalScale = fixture.scale;
  if (!finalScale || finalScale === 1.0) {
    try {
      // Import here to avoid circular dependencies
      const elementSizeDefaults = await import('@/services/elementSizeDefaultsService');

      const category = fixture.category;
      const subcategory = fixture.subcategory;

      if (category) {
        // Try to get default scale (subcategory has priority over category)
        const defaultScale = await elementSizeDefaults.getDefaultScale(
          'fixture',
          category,
          subcategory || undefined,
        );

        if (defaultScale) {
          finalScale = defaultScale;
          logger.info(
            `Applied default scale ${defaultScale} for ${category}${subcategory ? `/${subcategory}` : ''}`,
          );
        }
      }
    } catch (error) {
      // If fetching defaults fails, just log and continue without defaults
      logger.warn('Failed to fetch default scale:', error);
    }
  }

  const insertData = {
    ...fixture,
    scale: finalScale,
  };

  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_fixtures')
    .insert([insertData as any])
    .select()
    .single();

  if (error) {
    logger.error('[fixtureService.createFixture] Error:', error);
    throw error;
  }

  return data as unknown as LightingFixture;
}

/**
 * Update a lighting fixture
 */
export async function updateFixture(
  id: string,
  updates: Partial<Omit<LightingFixture, 'id' | 'created_at'>>,
): Promise<LightingFixture> {
  // If updating circuit_id, first get the old circuit_id
  let oldCircuitId: string | null = null;

  if ('circuit_id' in updates) {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data: oldFixture, error: getError } = await supabase
      .from('lighting_fixtures')
      .select('circuit_id')
      .eq('id', id)
      .single();

    if (getError) throw getError;
    oldCircuitId = oldFixture?.circuit_id;
  }

  // Update the fixture
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_fixtures')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // If circuit was changed and the old circuit exists, check if it's now empty
  if ('circuit_id' in updates && oldCircuitId && oldCircuitId !== updates.circuit_id) {
    const { deleted, error: deleteError } = await deleteEmptyCircuit(oldCircuitId);

    if (deleted) {
      logger.log(`Circuit ${oldCircuitId} was deleted as it had no remaining fixtures`);
    }

    if (deleteError) {
      logger.error('Error checking/deleting empty circuit:', deleteError);
    }
  }

  return data as unknown as LightingFixture;
}

/**
 * Delete a lighting fixture
 */
export async function deleteFixture(id: string): Promise<boolean> {
  // First get the fixture to check its circuit_id
  const supabase = await getAuthenticatedSupabaseClient();
  const { data: fixture, error: getError } = await supabase
    .from('lighting_fixtures')
    .select('circuit_id')
    .eq('id', id)
    .single();

  if (getError) throw getError;

  const circuitId = fixture?.circuit_id;

  // Delete the fixture
  const { error } = await supabase.from('lighting_fixtures').delete().eq('id', id);

  if (error) throw error;

  // If the fixture was assigned to a circuit, check if that circuit is now empty
  if (circuitId) {
    const { deleted, error: deleteError } = await deleteEmptyCircuit(circuitId);

    if (deleted) {
      logger.log(`Circuit ${circuitId} was deleted as it had no remaining fixtures`);
    }

    if (deleteError) {
      logger.error('Error checking/deleting empty circuit:', deleteError);
    }
  }

  return true;
}

/**
 * Clone a fixture
 */
export async function cloneFixture(
  fixtureId: string,
  fixtures: LightingFixture[],
): Promise<LightingFixture | null> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    // Find the fixture to clone
    const sourceFixture = fixtures.find(f => f.id === fixtureId);
    if (!sourceFixture) {
      toast.error('Fixture not found');
      return null;
    }

    // Create a slightly offset position for the new fixture (10 pixels to the right)
    const offsetX = sourceFixture.x_position + 10;
    const offsetY = sourceFixture.y_position;

    // Generate a new ID for the cloned fixture
    const newId = uuidv4();

    // Create the new fixture object
    const newFixture: LightingFixture = {
      id: newId,
      fixture_type: sourceFixture.fixture_type,
      x_position: offsetX,
      y_position: offsetY,
      wattage: sourceFixture.wattage,
      confidence_score: 1, // Set to 1 (100%) since it's manually created
      user_verified: true,
      circuit_id: sourceFixture.circuit_id,
      product_id: sourceFixture.product_id,
      floor_plan_id: sourceFixture.floor_plan_id,
      // Copy icon properties to preserve fixture appearance
      category: sourceFixture.category,
      category_info: sourceFixture.category_info,
      icon_style: sourceFixture.icon_style,
      subcategory: sourceFixture.subcategory,
      rotation: sourceFixture.rotation,
      scale: sourceFixture.scale,
      room_id: sourceFixture.room_id,
      created_at: new Date().toISOString(),
    };

    // Insert the new fixture into the database
    const { error } = await supabase.from('lighting_fixtures').insert([newFixture as any]);

    if (error) {
      throw error;
    }

    toast.success('Fixture cloned successfully');
    return newFixture;
  } catch (error) {
    logger.error('Error cloning fixture:', error);
    toast.error('Failed to clone fixture');
    return null;
  }
}

/**
 * Update fixture position
 */
export async function updateFixturePosition(
  id: string,
  x: number,
  y: number,
): Promise<LightingFixture | null> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('lighting_fixtures')
      .update({ x_position: x, y_position: y })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as unknown as LightingFixture;
  } catch (error) {
    logger.error('Error updating fixture position:', error);
    toast.error('Failed to update fixture position');
    return null;
  }
}

/**
 * Update fixture verification status
 */
export async function verifyFixture(fixtureId: string, verified: boolean): Promise<boolean> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { error } = await supabase
      .from('lighting_fixtures')
      .update({ user_verified: verified } as any)
      .eq('id', fixtureId);

    if (error) throw error;

    toast.success(`Fixture ${verified ? 'verified' : 'marked as incorrect'}`);
    return true;
  } catch (error) {
    logger.error('Error updating fixture verification:', error);
    toast.error('Failed to update fixture');
    return false;
  }
}

/**
 * Update fixture scale
 */
export async function updateFixtureScale(id: string, scale: number): Promise<LightingFixture> {
  // Validate scale range
  if (scale < 0.2 || scale > 5.0) {
    throw new Error('Scale must be between 0.2 and 5.0');
  }

  return updateFixture(id, { scale } as any);
}

/**
 * Update fixture rotation
 */
export async function updateFixtureRotation(
  id: string,
  rotation: number,
): Promise<LightingFixture> {
  // Normalize rotation to 0-360
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  return updateFixture(id, { rotation: normalizedRotation } as any);
}

/**
 * Batch update fixture scales
 */
export async function batchUpdateFixtureScale(ids: string[], scale: number): Promise<void> {
  if (scale < 0.2 || scale > 5.0) {
    throw new Error('Scale must be between 0.2 and 5.0');
  }

  const supabase = await getAuthenticatedSupabaseClient();
  const { error } = await supabase
    .from('lighting_fixtures')
    .update({ scale, updated_at: new Date().toISOString() } as any)
    .in('id', ids);

  if (error) {
    logger.error('Error batch updating fixture scales:', error);
    throw error;
  }
}

/**
 * Get fixtures by type for batch operations
 */
export async function getFixturesByType(
  floorPlanId: string,
  fixtureType: string,
): Promise<LightingFixture[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_fixtures')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .eq('fixture_type', fixtureType);

  if (error) throw error;
  return data as unknown as LightingFixture[];
}

/**
 * Get fixtures by category for batch operations
 */
export async function getFixturesByCategory(
  floorPlanId: string,
  category: string,
  subcategory?: string,
): Promise<LightingFixture[]> {
  const supabase = await getAuthenticatedSupabaseClient();

  let query = supabase
    .from('lighting_fixtures')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .eq('category', category);

  if (subcategory) {
    query = query.eq('subcategory', subcategory);
  }

  const { data, error } = await query;

  if (error) {
    logger.error('Error fetching fixtures by category:', error);
    throw error;
  }

  return data as unknown as LightingFixture[];
}

/**
 * Update strip fixture position (both endpoints together)
 * Used when dragging the entire strip to a new location
 */
export async function updateStripPosition(
  id: string,
  deltaX: number,
  deltaY: number,
  currentStartX: number,
  currentStartY: number,
  currentEndX: number,
  currentEndY: number,
): Promise<LightingFixture | null> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('lighting_fixtures')
      .update({
        x_position: currentStartX + deltaX,
        y_position: currentStartY + deltaY,
        end_x_position: currentEndX + deltaX,
        end_y_position: currentEndY + deltaY,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as unknown as LightingFixture;
  } catch (error) {
    logger.error('Error updating strip position:', error);
    toast.error('Failed to update strip position');
    return null;
  }
}

/**
 * Update a strip fixture's endpoint
 * Used for resizing strips by dragging the start or end handle
 */
export async function updateStripEndpoint(
  id: string,
  endpoint: 'start' | 'end',
  x: number,
  y: number,
): Promise<LightingFixture | null> {
  try {
    const supabase = await getAuthenticatedSupabaseClient();

    const updates =
      endpoint === 'start'
        ? { x_position: x, y_position: y }
        : { end_x_position: x, end_y_position: y };

    const { data, error } = await supabase
      .from('lighting_fixtures')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as unknown as LightingFixture;
  } catch (error) {
    logger.error('Error updating strip endpoint:', error);
    toast.error('Failed to update strip endpoint');
    return null;
  }
}

/**
 * Calculate strip length in millimeters
 * Requires floor plan scale data to convert from pixels
 */
export function calculateStripLengthMm(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  pixelsPerUnit: number,
  unit: 'mm' | 'cm' | 'm' | 'in' | 'ft',
): number {
  // Calculate pixel distance
  const pixelDistance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));

  // Convert to real-world distance in the scale unit
  const realDistance = pixelDistance / pixelsPerUnit;

  // Convert to millimeters based on unit
  switch (unit) {
    case 'mm':
      return realDistance;
    case 'cm':
      return realDistance * 10;
    case 'm':
      return realDistance * 1000;
    case 'in':
      return realDistance * 25.4;
    case 'ft':
      return realDistance * 304.8;
    default:
      return realDistance;
  }
}
